import {
  CSRF_HEADER,
  type HeadingAnchor,
  headingAnchors,
  headingFragment,
  type OpenWikiPageThreadRequest,
  type ProjectView,
  type WikiHeadingKey,
  type WikiPageThread,
  type WikiPageThreadKind,
  type WikiPageView,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, MessageSquare, MessageSquarePlus } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { createChat } from "../conversation/chat.ts";
import { useChatCache } from "../conversation/chat-cache.tsx";
import { refreshConversations } from "../conversation/queries.ts";
import { can, canChange } from "../projects/capabilities.ts";
import { Link, navigate, projectConversationPath, wikiPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { holdReload } from "../shell/build-refresh.ts";
import { wikiKeys } from "./queries.ts";
import { placeAnchors } from "./review-anchors.ts";
import { passageOf } from "./selection.ts";
import { Composer, DeleteCommentDialog, ThreadView } from "./ThreadPanel.tsx";

// Threads on published pages (wiki-collaboration D17): the rail's Discussion, the panel's, and
// the comment affordance beside each heading. Only what the server returns is shown: a guest
// gets no thread and sees no control, and an N-1 API (no route) shows nothing.

type Project = Pick<ProjectView, "capabilities" | "frozenAt"> & { id: string };
type Page = Pick<WikiPageView, "markdown" | "version" | "shownVersion"> & { id: string };

export const pageThreadKeys = {
  page: (projectId: string, pageId: string) =>
    [...wikiKeys.all(projectId), "page-threads", pageId] as const,
};

/** The page's threads as the viewer sees them; none without `wiki.comment` or on an N-1 API. */
export function usePageThreads(project: Project, pageId: string) {
  return useQuery({
    queryKey: pageThreadKeys.page(project.id, pageId),
    enabled: can(project, "wiki.comment"),
    retry: false,
    queryFn: async (): Promise<WikiPageThread[]> =>
      (
        await ok(
          await api.api.projects[":projectId"].wiki.pages[":pageId"].threads.$get({
            param: { projectId: project.id, pageId },
          }),
        )
      ).threads,
  });
}

type PageThreadWrite =
  | { kind: "open"; request: OpenWikiPageThreadRequest }
  | { kind: "reply"; threadId: string; body: string }
  | { kind: "resolve"; threadId: string; note?: string | undefined }
  | { kind: "reopen"; threadId: string }
  | { kind: "delete"; threadId: string; commentId: string };

/** Every write answers the page's threads; the bell's list may change too. */
export function usePageThreadWrite(projectId: string, pageId: string) {
  const queryClient = useQueryClient();
  const { csrfToken } = useSession();
  const headers = { [CSRF_HEADER]: csrfToken };
  return useMutation({
    mutationFn: async (write: PageThreadWrite): Promise<WikiPageThread[]> => {
      const route = api.api.projects[":projectId"].wiki.pages[":pageId"].threads;
      const param = { projectId, pageId };
      if (write.kind === "open") {
        return (await ok(await route.$post({ param, json: write.request }, { headers }))).threads;
      }
      const thread = route[":threadId"];
      const p = { ...param, threadId: write.threadId };
      if (write.kind === "reply") {
        return (
          await ok(
            await thread.comments.$post({ param: p, json: { body: write.body } }, { headers }),
          )
        ).threads;
      }
      if (write.kind === "delete") {
        return (
          await ok(
            await thread.comments[":commentId"].$delete(
              { param: { ...p, commentId: write.commentId } },
              { headers },
            ),
          )
        ).threads;
      }
      if (write.kind === "resolve") {
        return (
          await ok(
            await thread.resolve.$post(
              { param: p, json: write.note ? { note: write.note } : {} },
              { headers },
            ),
          )
        ).threads;
      }
      return (await ok(await thread.reopen.$post({ param: p }, { headers }))).threads;
    },
    onSuccess: (threads) => {
      queryClient.setQueryData(pageThreadKeys.page(projectId, pageId), threads);
    },
  });
}

/**
 * "Ask the agent" (D17): a new project conversation whose first message the server composes
 * from the thread; the conversation opens while the answer streams.
 */
function useAskAgent(projectId: string, pageId: string) {
  const { csrfToken } = useSession();
  const cache = useChatCache();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string) => {
      const release = holdReload();
      try {
        const created = await ok(
          await api.api.projects[":projectId"].conversations.$post(
            { param: { projectId } },
            { headers: { [CSRF_HEADER]: csrfToken } },
          ),
        );
        const chat = createChat(created.id, csrfToken, [], () => {
          refreshConversations(queryClient, created.id);
          void queryClient.invalidateQueries({ queryKey: pageThreadKeys.page(projectId, pageId) });
        });
        // The text is the server's; this one only fills the local bubble until it streams.
        void chat.sendMessage({ text: "…" }, { body: { ask: { projectId, pageId, threadId } } });
        cache.keep(created.id, chat, true);
        navigate(projectConversationPath(projectId, created.id));
      } finally {
        setTimeout(release, 0);
      }
    },
  });
}

/** "Ask the agent" and its conversation link under a page thread (D17), when offered. */
export function AskAgentFooter(props: {
  project: Pick<Project, "id">;
  pageId: string;
  thread: Pick<WikiPageThread, "id" | "mayAsk" | "askedConversationId">;
}) {
  const { t } = useTranslation();
  const ask = useAskAgent(props.project.id, props.pageId);
  const { thread } = props;
  if (!thread.askedConversationId && !thread.mayAsk) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {thread.mayAsk ? (
          <Button
            size="xs"
            variant="ghost"
            disabled={ask.isPending}
            onClick={() => ask.mutate(thread.id)}
          >
            <Bot aria-hidden="true" />
            {t("wiki.pageThreads.ask")}
          </Button>
        ) : null}
        {thread.askedConversationId ? (
          <Link
            href={projectConversationPath(props.project.id, thread.askedConversationId)}
            className="text-xs text-accent-text hover:underline"
          >
            {t("wiki.pageThreads.asked")}
          </Link>
        ) : null}
      </div>
      {ask.isError ? <InlineError>{ask.error.message}</InlineError> : null}
    </div>
  );
}

/** Headings of the current version by key, for the threads' anchor lines. */
function headingText(page: Page): Map<string, string> {
  return new Map(headingAnchors(page.markdown).map((h) => [`${h.occurrence}:${h.slug}`, h.text]));
}
const keyOf = (h: WikiHeadingKey) => `${h.occurrence}:${h.slug}`;
/** Open threads first, then the newest. */
const ordered = (threads: readonly WikiPageThread[]) =>
  [...threads].sort(
    (a, b) =>
      Number(a.status === "resolved") - Number(b.status === "resolved") ||
      b.createdAt.localeCompare(a.createdAt),
  );

/** The page's threads, one hairline list; each says where it sits. */
function ThreadList(props: {
  project: Project;
  page: Page;
  threads: readonly WikiPageThread[];
  /** Without the anchor line: every thread is on the one heading being shown. */
  sameAnchor?: boolean;
}) {
  const { t } = useTranslation();
  const { project, page, threads } = props;
  const write = usePageThreadWrite(project.id, page.id);
  const ask = useAskAgent(project.id, page.id);
  const [remove, setRemove] = useState<{ threadId: string; commentId: string } | null>(null);
  const headings = useMemo(() => headingText(page), [page]);
  return (
    <>
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
        {ordered(threads).map((thread) => (
          <ThreadView
            key={thread.id}
            thread={thread}
            busy={write.isPending || ask.isPending}
            onReply={(body) => write.mutateAsync({ kind: "reply", threadId: thread.id, body })}
            onResolve={(resolved, note) =>
              write.mutateAsync(
                resolved
                  ? { kind: "resolve", threadId: thread.id, note }
                  : { kind: "reopen", threadId: thread.id },
              )
            }
            onDelete={(commentId) => setRemove({ threadId: thread.id, commentId })}
            meta={
              props.sameAnchor && !thread.detached ? null : (
                <p className="text-xs text-fg-muted">
                  {thread.detached ? (
                    <>
                      {t("wiki.pageThreads.detached")}{" "}
                      <Link
                        href={wikiPath(project.id, page.id, thread.anchoredVersion)}
                        // Inside a line of text: underlined, not told apart by colour alone.
                        className="text-accent-text underline underline-offset-4"
                      >
                        {t("wiki.pageThreads.onVersion", { version: thread.anchoredVersion })}
                      </Link>
                    </>
                  ) : thread.anchor?.quote ? (
                    // The passage it sits on (wiki-collaboration-ux D2): tenant content.
                    <span data-esi-content="wiki" className="break-words">
                      {t("wiki.review.pane.on")} “
                      <span className="text-fg-secondary">{thread.anchor.quote}</span>”
                    </span>
                  ) : thread.heading ? (
                    t("wiki.pageThreads.onHeading", {
                      heading: headings.get(keyOf(thread.heading)) ?? thread.heading.slug,
                    })
                  ) : (
                    t("wiki.pageThreads.onPage")
                  )}
                </p>
              )
            }
            footer={
              thread.askedConversationId || thread.mayAsk ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {thread.mayAsk ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={ask.isPending}
                      onClick={() => ask.mutate(thread.id)}
                    >
                      <Bot aria-hidden="true" />
                      {t("wiki.pageThreads.ask")}
                    </Button>
                  ) : null}
                  {thread.askedConversationId ? (
                    <Link
                      href={projectConversationPath(project.id, thread.askedConversationId)}
                      className="text-xs text-accent-text hover:underline"
                    >
                      {t("wiki.pageThreads.asked")}
                    </Link>
                  ) : null}
                </div>
              ) : null
            }
          />
        ))}
      </ul>
      {write.isError && !remove ? <InlineError>{write.error.message}</InlineError> : null}
      {ask.isError ? <InlineError>{ask.error.message}</InlineError> : null}
      {remove ? (
        <DeleteCommentDialog
          busy={write.isPending}
          error={write.isError ? write.error.message : null}
          onClose={() => setRemove(null)}
          onDelete={() => write.mutateAsync({ kind: "delete", ...remove })}
        />
      ) : null}
    </>
  );
}

/**
 * The passage a heading's composer quotes: the heading as the page renders it, with the text
 * around it as context (as a selection reads it), so a heading whose words repeat keeps its
 * place; else its words alone; none for a heading with no words (the page as a whole).
 */
function headingPassage(heading: HeadingAnchor, prefix = "wiki-section") {
  const element = document.getElementById(`${prefix}-${headingFragment(heading)}`);
  const container = element?.closest<HTMLElement>("[data-reading-page], article");
  if (element && container) {
    const range = document.createRange();
    range.selectNodeContents(element);
    const passage = passageOf(container, range);
    if (passage) {
      return {
        anchor: {
          quote: passage.quote,
          prefix: passage.prefix,
          suffix: passage.suffix,
          section: heading.slug,
        },
      };
    }
  }
  const quote = Array.from(heading.text.trim()).slice(0, 500).join("");
  return quote ? { anchor: { quote, prefix: "", suffix: "", section: heading.slug } } : {};
}

/** A new page thread: a comment or a question, on `heading` or the page as a whole. */
function NewPageThread(props: {
  projectId: string;
  pageId: string;
  /** The heading it is on: a passage thread quoting the heading's text (S4). */
  heading: HeadingAnchor | null;
  /** The renderer's heading id prefix, to read the heading's context on the page. */
  headingPrefix?: string;
  onDone?: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const write = usePageThreadWrite(props.projectId, props.pageId);
  const [kind, setKind] = useState<WikiPageThreadKind>("comment");
  return (
    <>
      <Composer
        label={props.heading ? t("wiki.pageThreads.bodyHeading") : t("wiki.pageThreads.body")}
        submit={t("wiki.threads.post")}
        busy={write.isPending}
        {...(props.onCancel && { onCancel: props.onCancel })}
        onPost={async (body) => {
          await write.mutateAsync({
            kind: "open",
            request: {
              kind,
              body,
              ...(props.heading && headingPassage(props.heading, props.headingPrefix)),
            },
          });
          props.onDone?.();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-kind`} className="text-sm font-medium">
            {t("wiki.threads.kindLabel")}
          </Label>
          <NativeSelect
            id={`${id}-kind`}
            value={kind}
            disabled={write.isPending}
            onChange={(e) => setKind(e.currentTarget.value as WikiPageThreadKind)}
          >
            {(["comment", "question"] as const).map((k) => (
              <option key={k} value={k}>
                {t(`wiki.threads.kindOption.${k}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
      </Composer>
      {write.isError ? <InlineError>{write.error.message}</InlineError> : null}
    </>
  );
}

/**
 * The page's Discussion (D17): in the reading view's rail and under the article in the wiki
 * panel. Its count is the open threads; open ones come first. Nothing for a guest.
 */
export function PageDiscussion(props: {
  project: Project;
  page: Page;
  /** The rail's small type, or the panel's body type. */
  variant: "rail" | "panel";
}) {
  const { t } = useTranslation();
  const { project, page } = props;
  const headingId = useId();
  const threads = usePageThreads(project, page.id);
  const [composing, setComposing] = useState(false);
  if (!can(project, "wiki.comment") || threads.isError) {
    return null;
  }
  const all = threads.data ?? [];
  const open = all.filter((thread) => thread.status === "open").length;
  const writable = canChange(project, "wiki.comment");
  return (
    <section data-esi-content="wiki" aria-labelledby={headingId} className="flex flex-col gap-2.5">
      <h2
        id={headingId}
        className={
          props.variant === "rail"
            ? "flex items-baseline gap-2 text-xs font-semibold text-fg"
            : "flex items-baseline gap-2 text-sm font-semibold text-fg"
        }
      >
        {t("wiki.pageThreads.title")}
        {open ? (
          <span className="text-xs font-normal text-fg-muted tabular-nums">
            {t("wiki.pageThreads.open", { count: open })}
          </span>
        ) : null}
      </h2>
      {all.length ? (
        <ThreadList project={project} page={page} threads={all} />
      ) : threads.isPending ? null : (
        <p className="text-xs text-fg-secondary">{t("wiki.pageThreads.none")}</p>
      )}
      {writable ? (
        composing ? (
          <NewPageThread
            projectId={project.id}
            pageId={page.id}
            heading={null}
            onDone={() => setComposing(false)}
            onCancel={() => setComposing(false)}
          />
        ) : (
          <div>
            <Button size="xs" variant="ghost" onClick={() => setComposing(true)}>
              <MessageSquarePlus aria-hidden="true" />
              {t("wiki.pageThreads.comment")}
            </Button>
          </div>
        )
      ) : null}
    </section>
  );
}

/**
 * A thread on this heading: a heading thread from before S4 (no quote yet), or a passage thread
 * quoting the heading's words that the page places on this very heading (its context keeps two
 * "## Notes" apart). Other passages carry their own markers in the text.
 */
function onHeading(
  t: WikiPageThread,
  anchor: HeadingAnchor,
  placedBlock: ReadonlyMap<string, number | null>,
) {
  if (!t.anchor?.quote) {
    return t.heading?.slug === anchor.slug && t.heading.occurrence === anchor.occurrence;
  }
  const quote = t.anchor.quote.trim();
  return (
    t.anchor.section === anchor.slug &&
    !!quote &&
    anchor.text.trim().startsWith(quote) &&
    placedBlock.get(t.id) === anchor.block
  );
}

/**
 * The comment affordance beside a heading (D17): its open thread count when it has any, and a
 * dialog with the heading's threads and a composer anchored to it. Nothing for a guest, nor on
 * an older version (threads are anchored on the current one).
 */
export function useHeadingAside(project: Project, page: Page, headingPrefix = "wiki-section") {
  const threads = usePageThreads(project, page.id);
  // Where each quoting thread reads on the page, by top-level block (the heading it is on).
  const quoting = useMemo(
    () => (threads.data ?? []).filter((t) => !t.detached && t.anchor?.quote),
    [threads.data],
  );
  const placements = useMemo(
    () =>
      quoting.length
        ? placeAnchors(
            page.markdown,
            quoting.map((t) => ({
              quote: t.anchor?.quote ?? "",
              prefix: t.anchor?.prefix ?? "",
              suffix: t.anchor?.suffix ?? "",
            })),
          )
        : [],
    [page.markdown, quoting],
  );
  const placedBlock = new Map(quoting.map((t, i) => [t.id, placements[i]?.block ?? null]));
  const [shown, setShown] = useState<HeadingAnchor | null>(null);
  const available =
    can(project, "wiki.comment") && !threads.isError && page.shownVersion === page.version;
  const render = (anchor: HeadingAnchor) =>
    available ? (
      <HeadingButton
        anchor={anchor}
        count={
          (threads.data ?? []).filter(
            (t) => !t.detached && t.status === "open" && onHeading(t, anchor, placedBlock),
          ).length
        }
        onOpen={() => setShown(anchor)}
      />
    ) : null;
  const dialog = shown ? (
    <HeadingThreadsDialog
      project={project}
      page={page}
      anchor={shown}
      threads={(threads.data ?? []).filter((t) => !t.detached && onHeading(t, shown, placedBlock))}
      headingPrefix={headingPrefix}
      onClose={() => setShown(null)}
    />
  ) : null;
  return { render: available ? render : undefined, dialog };
}

function HeadingButton(props: { anchor: HeadingAnchor; count: number; onOpen: () => void }) {
  const { t } = useTranslation();
  const label = props.count
    ? t("wiki.pageThreads.headingThreads", { count: props.count, heading: props.anchor.text })
    : t("wiki.pageThreads.commentOn", { heading: props.anchor.text });
  return (
    <Button
      variant="ghost"
      size="xs"
      // Not part of the page's text: a passage's context never reads its count.
      data-selection-ignore=""
      aria-label={label}
      title={label}
      onClick={props.onOpen}
      // Quiet until the heading is hovered or focused, unless it already has a discussion.
      className={
        props.count
          ? "text-fg-secondary"
          : "text-fg-muted opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
      }
    >
      <MessageSquare aria-hidden="true" />
      {props.count ? <span className="tabular-nums">{props.count}</span> : null}
    </Button>
  );
}

function HeadingThreadsDialog(props: {
  project: Project;
  page: Page;
  anchor: HeadingAnchor;
  headingPrefix: string;
  threads: readonly WikiPageThread[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { project, page, anchor, threads } = props;
  return (
    <AppDialog
      open
      onClose={props.onClose}
      title={t("wiki.pageThreads.dialogTitle", { heading: anchor.text })}
      description={t("wiki.pageThreads.dialogDescription")}
    >
      <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
        {threads.length ? (
          <ThreadList project={project} page={page} threads={threads} sameAnchor />
        ) : null}
        {canChange(project, "wiki.comment") ? (
          <NewPageThread
            projectId={project.id}
            pageId={page.id}
            heading={anchor}
            headingPrefix={props.headingPrefix}
          />
        ) : null}
      </div>
    </AppDialog>
  );
}
